import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import ServiceToggle from "./ServiceToggle";
import { 
  Share2, 
  Copy, 
  Check, 
  RefreshCw, 
  DollarSign,
  Link2,
  Unlink,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";

const ServiceSharingModal = ({ isOpen, onClose, service, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [sharingStatus, setSharingStatus] = useState(null);
  const [commission, setCommission] = useState(15);
  const [copied, setCopied] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // 'enable', 'disable', 'rotate', 'commission'

  useEffect(() => {
    if (isOpen && service?.id) {
      loadSharingStatus();
    }
  }, [isOpen, service?.id]);

  const loadSharingStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/services/${service.id}/sharing`);
      setSharingStatus(response.data);
      if (response.data?.sharing?.commission) {
        setCommission(response.data.sharing.commission);
      }
    } catch (error) {
      console.error("Error loading sharing status:", error);
      // If no sharing configured yet, that's OK
      setSharingStatus({ sharing: { enabled: false } });
    } finally {
      setLoading(false);
    }
  };

  const handleEnableSharing = async () => {
    try {
      setActionLoading('enable');
      const response = await api.post(`/services/${service.id}/sharing/enable`, {
        commission: commission
      });
      toast.success(`Sharing enabled! Token: ${response.data.shareToken.substring(0, 20)}...`);
      loadSharingStatus();
      onUpdate?.();
    } catch (error) {
      console.error("Error enabling sharing:", error);
      toast.error(error.response?.data?.detail || "Failed to enable sharing");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisableSharing = async () => {
    try {
      setActionLoading('disable');
      await api.post(`/services/${service.id}/sharing/disable`);
      toast.success("Sharing disabled. All reseller links have been revoked.");
      loadSharingStatus();
      onUpdate?.();
    } catch (error) {
      console.error("Error disabling sharing:", error);
      toast.error(error.response?.data?.detail || "Failed to disable sharing");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRotateToken = async () => {
    try {
      setActionLoading('rotate');
      const response = await api.post(`/services/${service.id}/sharing/rotate-token`);
      toast.success("New token generated! Old token is now invalid.");
      loadSharingStatus();
    } catch (error) {
      console.error("Error rotating token:", error);
      toast.error(error.response?.data?.detail || "Failed to rotate token");
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateCommission = async () => {
    try {
      setActionLoading('commission');
      await api.put(`/services/${service.id}/sharing/commission`, {
        commission: commission
      });
      toast.success(`Commission updated to ${commission}%`);
      loadSharingStatus();
    } catch (error) {
      console.error("Error updating commission:", error);
      toast.error(error.response?.data?.detail || "Failed to update commission");
    } finally {
      setActionLoading(null);
    }
  };

  const copyToken = () => {
    const token = sharingStatus?.sharing?.token;
    if (token) {
      navigator.clipboard.writeText(token);
      setCopied(true);
      toast.success("Token copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isEnabled = sharingStatus?.sharing?.enabled;
  const token = sharingStatus?.sharing?.token;
  const resellerCount = sharingStatus?.sharing?.resellerCount || 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-600" />
            Service Sharing Settings
          </DialogTitle>
          <DialogDescription>
            Allow other companies to resell &ldquo;{service?.name}&rdquo; on their platforms
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isEnabled ? 'bg-green-100' : 'bg-gray-200'}`}>
                  {isEnabled ? (
                    <Link2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Unlink className="w-5 h-5 text-gray-500" />
                  )}
                </div>
                <div>
                  <p className="font-medium">{isEnabled ? 'Sharing Enabled' : 'Sharing Disabled'}</p>
                  <p className="text-sm text-gray-500">
                    {isEnabled ? `${resellerCount} reseller(s) linked` : 'No resellers can access this service'}
                  </p>
                </div>
              </div>
              <ServiceToggle
                checked={isEnabled}
                onChange={(checked) => {
                  if (checked) {
                    handleEnableSharing();
                  } else {
                    handleDisableSharing();
                  }
                }}
                disabled={actionLoading === 'enable' || actionLoading === 'disable'}
              />
            </div>

            {isEnabled && (
              <>
                {/* Share Token */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Share Token</Label>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <Input
                        value={token || ''}
                        readOnly
                        className="pr-10 font-mono text-sm bg-gray-50"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={copyToken}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-500" />
                        )}
                      </Button>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRotateToken}
                      disabled={actionLoading === 'rotate'}
                      className="shrink-0"
                    >
                      {actionLoading === 'rotate' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Share this token with partners who want to resell this service. Rotating the token will disconnect all current resellers.
                  </p>
                </div>

                {/* Commission Settings */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Reseller Commission</Label>
                  <div className="flex gap-2 items-center">
                    <div className="relative flex-1">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="number"
                        min="0"
                        max="50"
                        value={commission}
                        onChange={(e) => setCommission(Number(e.target.value))}
                        className="pl-9 pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUpdateCommission}
                      disabled={actionLoading === 'commission' || commission === sharingStatus?.sharing?.commission}
                    >
                      {actionLoading === 'commission' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Update'
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Resellers will receive this percentage of each booking. You keep the rest minus platform fees.
                  </p>
                </div>

                {/* Concise Info */}
                <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
                  💡 Resellers link via token → Customers book → Payment auto-splits → You see all bookings
                </p>
              </>
            )}

            {!isEnabled && (
              <>
                {/* Commission Input (when disabled, for setting before enabling) */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Commission for Resellers</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      max="50"
                      value={commission}
                      onChange={(e) => setCommission(Number(e.target.value))}
                      placeholder="15"
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                  </div>
                </div>
                
                {/* Concise Tips */}
                <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                  ⚡ Typical commission: 10-20% • Share only with trusted partners • Revoke anytime by rotating token
                </p>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ServiceSharingModal;
